import { FC, useEffect, useRef } from 'react';

type Segment = {
	value: number;
	color: string;
};

type Props = {
	data1: Segment;
	data2: Segment;
	data1Name?: string;
	data2Name?: string;
	showFooter?: boolean;
};

const DashboardManagedByChart: FC<Props> = ({
	data1,
	data2,
	data1Name = 'Data 1',
	data2Name = 'Data 2',
	showFooter = true,
}) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	const totalValue = data1.value + data2.value;
	const data1Percentage = ((data1.value / totalValue) * 100).toFixed(0);
	const data2Percentage = ((data2.value / totalValue) * 100).toFixed(0);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const width = canvas.width;
		const height = canvas.height;
		const centerX = width / 2;
		const centerY = height / 2;
		const radius = Math.min(width, height) / 2 - 10;
		const lineWidth = 15;
		const gapAngle = 0.2;

		ctx.clearRect(0, 0, width, height);
		ctx.lineWidth = lineWidth;
		ctx.lineCap = 'round';

		let startAngle = -Math.PI / 2;

		const data = [data2, data1];

		data.forEach((segment) => {
			const angle =
				(segment.value / totalValue) *
				(2 * Math.PI - gapAngle * data.length);
			ctx.beginPath();
			ctx.strokeStyle = segment.color;
			ctx.arc(centerX, centerY, radius, startAngle, startAngle + angle);
			ctx.stroke();
			startAngle += angle + gapAngle;
		});
	}, [data1, data2]);

	return (
		<div className='flex flex-col gap-4'>
			<div className='relative w-60 h-60'>
				<canvas
					ref={canvasRef}
					width={240}
					height={240}
					className='w-full h-full'
				/>
				<div className='absolute inset-0 flex justify-center items-center'>
					<div className='flex flex-col items-center'>
						<span className='text-base font-medium'>{`${data1Percentage} %`}</span>
						<span className='text-sm text-popover-600'>
							{data1Name}
						</span>
					</div>
					<div className='h-8 mx-4 border-r border-popover-100' />
					<div className='flex flex-col items-center'>
						<span className='text-base font-medium'>{`${data2Percentage} %`}</span>
						<span className='text-sm text-popover-600'>
							{data2Name}
						</span>
					</div>
				</div>
			</div>
			{showFooter && (
				<div className='flex justify-center items-center gap-4'>
					<div className='flex items-center gap-1'>
						<div
							className='size-2 m-1.5 rounded-full'
							style={{ backgroundColor: data1.color }}
						/>
						<span className='text-sm text-popover-1000'>
							{data1Name}
						</span>
					</div>
					<div className='flex items-center gap-1'>
						<div
							className='size-2 m-1.5 rounded-full'
							style={{ backgroundColor: data2.color }}
						/>
						<span className='text-sm text-popover-1000'>
							{data2Name}
						</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default DashboardManagedByChart;
